import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getTokens, getStats, getChains, getSources, deleteToken } from '../services/api';
import NavRail from '../components/NavRail';
import Filters from '../components/Filters';
import TokenModal from '../components/TokenModal';
import OutcomesBar from '../components/OutcomesBar';
import CopyBtn from '../components/CopyBtn';
import usePersistedState from '../hooks/usePersistedState';

/* ============================================================
   TOKENS — Backoffice bot memecoin Solana (V1)
   Look = mockup design-docs/mockups/Tokens.jsx (dark terminal).
   Comportement = 100% des features réelles (filtres, liste paginée,
   modal détail, delete, stats en-têtes, polling). Câblé sur l'API.
   ============================================================ */

const CSS = `
@import url('https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@400;500;600;700;800&display=swap');
:root{--bg:#0a0b0e;--surface:#14161c;--surface2:#191c23;--border:#1f2329;--text:#e6e8ec;--text2:#8a8f98;
  --green:#00e08a;--red:#ff3b5c;--amber:#ffb020;--cyan:#22d3ee;}
.ck,.ck *{font-family:'JetBrains Mono',ui-monospace,monospace;box-sizing:border-box;}
.ck{background:var(--bg);color:var(--text);height:100vh;display:flex;overflow:hidden;font-size:13px;line-height:1.35;-webkit-font-smoothing:antialiased;}
.sb{width:64px;flex:0 0 64px;border-right:1px solid var(--border);background:#0c0d11;display:flex;flex-direction:column;align-items:center;padding:14px 0;gap:6px;}
.sb-logo{width:34px;height:34px;border-radius:8px;background:var(--red);color:#fff;display:flex;align-items:center;justify-content:center;font-weight:800;font-size:16px;margin-bottom:14px;box-shadow:0 0 14px rgba(255,59,92,.45);}
.sb-item{width:40px;height:40px;border-radius:8px;display:flex;align-items:center;justify-content:center;color:var(--text2);cursor:pointer;position:relative;transition:all .12s;font-size:16px;border:none;background:transparent;}
.sb-item:hover{background:var(--surface);color:var(--text);}
.sb-item.on{background:var(--surface);color:var(--cyan);box-shadow:inset 2px 0 0 var(--cyan);}
.sb-item .tip{position:absolute;left:52px;top:50%;transform:translateY(-50%);background:#000;border:1px solid var(--border);padding:4px 9px;border-radius:6px;font-size:11px;white-space:nowrap;opacity:0;pointer-events:none;transition:opacity .12s;z-index:50;color:var(--text);}
.sb-item:hover .tip{opacity:1;}
.sb-spacer{flex:1;}
.main{flex:1;display:flex;flex-direction:column;overflow:hidden;}
.top{display:flex;align-items:center;gap:18px;padding:11px 18px;border-bottom:1px solid var(--border);background:#0c0d11;flex:0 0 auto;}
.top-title{font-size:15px;font-weight:800;letter-spacing:.04em;display:flex;align-items:center;gap:9px;}
.top-title .ic{color:var(--cyan);}
.top-spacer{flex:1;}
.clock{font-size:11px;color:var(--text2);text-align:right;}.clock b{color:var(--text);font-weight:600;}
.page{flex:1;overflow:auto;padding:16px;}
.bar-row{display:flex;align-items:center;justify-content:space-between;margin-bottom:14px;flex-wrap:wrap;gap:10px;}
.h1{font-size:13px;letter-spacing:.16em;text-transform:uppercase;color:var(--text2);font-weight:600;display:flex;align-items:center;gap:10px;}
.scan-live{font-size:11px;color:var(--cyan);display:flex;align-items:center;gap:6px;letter-spacing:.04em;}
.scan-live .d{width:7px;height:7px;border-radius:50%;background:var(--cyan);animation:pg 1.4s infinite;}
@keyframes pg{0%,100%{opacity:1;}50%{opacity:.3;}}
.refresh{background:var(--surface);border:1px solid var(--border);color:var(--text2);font-family:inherit;font-size:11px;font-weight:600;padding:7px 13px;border-radius:8px;cursor:pointer;letter-spacing:.03em;transition:all .12s;}
.refresh:hover{color:var(--text);border-color:var(--cyan);}
.refresh:disabled{opacity:.5;cursor:not-allowed;}

/* stats cards (vraies stats getStats) */
.rules{display:flex;gap:10px;margin-bottom:14px;flex-wrap:wrap;}
.rule{background:var(--surface);border:1px solid var(--border);border-radius:9px;padding:9px 13px;flex:1;min-width:120px;}
.rule .k{font-size:9.5px;color:var(--text2);letter-spacing:.07em;text-transform:uppercase;}
.rule .v{font-size:18px;font-weight:700;margin-top:3px;}
.rule .v.green{color:var(--green);}.rule .v.red{color:var(--red);}.rule .v.cyan{color:var(--cyan);}

/* filtres natifs (Filters component) re-skinnés dans le scope terminal */
.filters-wrap{background:var(--surface);border:1px solid var(--border);border-radius:12px;padding:4px 14px 14px;margin-bottom:14px;}
.filters-wrap .filters-container{background:transparent;border:none;box-shadow:none;padding:0;margin:0;}
.filters-wrap .filters-header{display:flex;align-items:center;justify-content:space-between;padding:10px 0;border:none;}
.filters-wrap .filters-header h2{font-size:11px;letter-spacing:.14em;text-transform:uppercase;color:var(--text2);font-weight:600;margin:0;}
.filters-wrap .reset-btn{background:var(--surface2);border:1px solid var(--border);color:var(--text2);font-family:inherit;font-size:11px;font-weight:600;padding:6px 12px;border-radius:7px;cursor:pointer;}
.filters-wrap .reset-btn:hover{color:var(--text);border-color:var(--cyan);}
.filters-wrap .filters-grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(190px,1fr));gap:10px;}
.filters-wrap .filter-group{display:flex;flex-direction:column;gap:5px;}
.filters-wrap .filter-group label{font-size:9.5px;color:var(--text2);letter-spacing:.06em;text-transform:uppercase;font-weight:600;}
.filters-wrap .filter-group input,.filters-wrap .filter-group select{background:var(--surface2);border:1px solid var(--border);color:var(--text);font-family:inherit;font-size:12px;padding:7px 9px;border-radius:7px;outline:none;}
.filters-wrap .filter-group input:focus,.filters-wrap .filter-group select:focus{border-color:var(--cyan);}
.filters-wrap .filter-group input::placeholder{color:var(--text2);opacity:.7;}
.filters-wrap .filter-group select option{background:var(--surface);color:var(--text);}

.tbl-wrap{background:var(--surface);border:1px solid var(--border);border-radius:12px;overflow:hidden;}
.tbl-hd{display:flex;align-items:center;justify-content:space-between;padding:12px 15px;border-bottom:1px solid var(--border);gap:10px;flex-wrap:wrap;}
.tbl-hd .ti{font-size:11px;letter-spacing:.14em;text-transform:uppercase;color:var(--text2);font-weight:600;}
.tbl-hd .ct{font-size:11px;color:var(--text2);}
table{width:100%;border-collapse:collapse;}
thead th{font-size:9px;letter-spacing:.09em;text-transform:uppercase;color:var(--text2);text-align:right;padding:8px 14px;font-weight:600;background:#0e1014;position:sticky;top:0;z-index:2;}
thead th:first-child{text-align:left;}
thead th.act{text-align:center;}
thead th.sortable{cursor:pointer;user-select:none;transition:color .12s;}
thead th.sortable:hover{color:var(--text);}
thead th.sorted{color:var(--cyan);}
thead th .arr{margin-left:3px;font-size:8px;}
tbody td{padding:9px 14px;text-align:right;font-size:12.5px;border-top:1px solid #16191f;vertical-align:middle;}
tbody td:first-child{text-align:left;}
tbody tr:hover{background:var(--surface2);cursor:pointer;}
.tk{display:flex;flex-direction:column;gap:1px;}.tk b{font-weight:700;font-size:13px;}.tk .mint{font-size:9.5px;color:var(--text2);}
.tk .sym{font-size:10px;color:var(--text2);}
.curve{display:flex;align-items:center;gap:7px;justify-content:flex-end;}
.curve-bar{width:48px;height:6px;background:#0a0b0e;border-radius:3px;overflow:hidden;border:1px solid var(--border);}
.curve-fill{height:100%;background:var(--cyan);}
.score{font-weight:800;}.score.hi{color:var(--green);}.score.mid{color:var(--amber);}.score.lo{color:var(--red);}
.dev{font-weight:600;}.dev.hi{color:var(--red);}.dev.ok{color:var(--text);}
.tag{font-size:9.5px;font-weight:700;padding:2px 8px;border-radius:5px;letter-spacing:.03em;white-space:nowrap;}
.tag.buy{background:rgba(0,224,138,.14);color:var(--green);}
.tag.watch{background:rgba(34,211,238,.14);color:var(--cyan);}
.tag.skip{background:rgba(255,59,92,.16);color:var(--red);}
.tag.none{background:rgba(138,143,152,.16);color:var(--text2);}
.muted{color:var(--text2);}
.acts{display:flex;gap:6px;justify-content:center;}
.act-btn{background:var(--surface2);border:1px solid var(--border);color:var(--text2);font-family:inherit;font-size:11px;padding:5px 9px;border-radius:6px;cursor:pointer;transition:all .12s;text-decoration:none;display:inline-flex;align-items:center;}
.act-btn:hover{color:var(--text);border-color:var(--cyan);}
.act-btn.del:hover{color:var(--red);border-color:var(--red);}
.act-btn:disabled{opacity:.5;cursor:not-allowed;}

.state{padding:40px 16px;text-align:center;color:var(--text2);}
.state .ic{font-size:26px;margin-bottom:8px;}
.state h3{font-size:14px;color:var(--text);margin-bottom:6px;font-weight:700;}
.spinner{width:26px;height:26px;border:3px solid var(--border);border-top-color:var(--cyan);border-radius:50%;animation:spin .8s linear infinite;margin:0 auto 10px;}
@keyframes spin{to{transform:rotate(360deg);}}

.pager{display:flex;align-items:center;justify-content:center;gap:8px;padding:14px;flex-wrap:wrap;}
.pg-btn{background:var(--surface2);border:1px solid var(--border);color:var(--text2);font-family:inherit;font-size:11px;font-weight:600;padding:7px 12px;border-radius:7px;cursor:pointer;}
.pg-btn:hover:not(:disabled){color:var(--text);border-color:var(--cyan);}
.pg-btn:disabled{opacity:.4;cursor:not-allowed;}
.pg-num{background:var(--surface2);border:1px solid var(--border);color:var(--text2);font-family:inherit;font-size:11px;font-weight:600;width:30px;height:30px;border-radius:7px;cursor:pointer;}
.pg-num.active{background:rgba(34,211,238,.14);color:var(--cyan);border-color:var(--cyan);}
.pg-info{font-size:11px;color:var(--text2);margin:0 8px;}
`;

const fmtNum = (n) => {
  if (n == null) return '—';
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(2)}K`;
  return Number(n).toFixed(2);
};
const fmtAge = (m) => {
  if (m == null) return '—';
  if (m < 60) return `${Math.floor(m)}min`;
  if (m < 1440) return `${Math.floor(m / 60)}h${Math.floor(m % 60)}`;
  return `${Math.floor(m / 1440)}j${Math.floor((m % 1440) / 60)}h`;
};
const fmtSol = (n) => (n == null ? '—' : Number(n).toFixed(2));
const fmtPriceSol = (n) => {
  if (n == null) return '—';
  if (n === 0) return '0';
  return Number(n).toExponential(2);
};
const ageMinFromCreated = (created_at) => {
  if (!created_at) return null;
  const ms = Date.now() - new Date(created_at).getTime();
  return ms > 0 ? ms / 60000 : 0;
};
const rugColor = (v) =>
  v == null ? 'var(--text2)' : v >= 20 ? 'var(--red)' : v >= 8 ? 'var(--amber)' : 'var(--text)';

// Colonnes triables (server-side, mêmes clés que sort_by backend ; 'age' → created_at)
const SORT_COLS = [
  { key: 'name', label: 'Token', align: 'left' },
  { key: 'pnl', label: 'PnL %' },
  { key: 'bonding_curve_progress', label: 'Bonding %' },
  { key: 'real_liq_sol', label: 'Liq SOL' },
  { key: 'unique_wallets', label: 'Wallets' },
  { key: 'rug_score', label: 'Rug' },
  { key: 'buy_speed', label: 'Buy spd' },
  { key: 'price_sol', label: 'Prix SOL' },
  { key: 'age', label: 'Âge' },
  { key: 'peak', label: 'Peak%' },
];

const EMPTY_FILTERS = {
  chain: '', recommendation: '', is_honeypot: '', is_rug: '', risk_level: '',
  has_social: '', has_logo: '', lp_locked: '', is_open_source: '', source: '',
  buy_sell_ratio: '', txn_volume: '', min_txn_buys: '', min_txn_sells: '',
  min_txn_total: '', team_balance: '', date_from: '', date_to: '', search: '',
  // Filtres outcomes (token_outcomes ⋈ bc_pda=address)
  min_peak: '', oc_migrated: '', oc_rugged: '', tp_min: '', tp_max: '',
  oc_life_min: '', oc_life_max: '',
  sort_by: 'created_at', sort_order: 'desc',
};

function TokensPage() {
  const [clock, setClock] = useState(new Date());
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedTokenId, setSelectedTokenId] = useState(null);
  const [deletingTokenId, setDeletingTokenId] = useState(null);
  const [filters, setFilters] = usePersistedState('tokens.filters', EMPTY_FILTERS);
  const itemsPerPage = 100;

  useEffect(() => {
    const id = setInterval(() => setClock(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  // ---- Queries (polling + cache, identique à l'ancienne page) ----
  const {
    data: tokensResponse, isLoading: tokensLoading, isFetching: tokensFetching,
    isError: tokensError, error: tokensErrorObj, refetch: refetchTokens,
  } = useQuery({
    queryKey: ['tokens', filters, currentPage],
    queryFn: () => getTokens({
      ...filters,
      skip: (currentPage - 1) * itemsPerPage,
      limit: itemsPerPage,
    }),
    staleTime: 30 * 1000,
    cacheTime: 5 * 60 * 1000,
  });

  const { data: statsResponse } = useQuery({
    queryKey: ['stats', filters],
    queryFn: () => getStats(filters),
    staleTime: 60 * 1000,
    cacheTime: 5 * 60 * 1000,
  });

  const { data: chains } = useQuery({
    queryKey: ['chains'], queryFn: getChains, staleTime: 5 * 60 * 1000,
  });

  const { data: sources } = useQuery({
    queryKey: ['sources'], queryFn: getSources, staleTime: 5 * 60 * 1000,
  });

  const tokens = tokensResponse?.tokens || [];
  const totalTokens = tokensResponse?.total || 0;
  const totalPages = Math.max(1, Math.ceil(totalTokens / itemsPerPage));
  const stats = statsResponse || {};

  // counts par recommandation sur la page courante (pour le bandeau)
  const counts = tokens.reduce((a, x) => {
    a[x.recommendation] = (a[x.recommendation] || 0) + 1;
    return a;
  }, {});

  const handleFilterChange = (patch) => {
    setFilters((prev) => ({ ...prev, ...patch }));
    setCurrentPage(1);
  };
  const handleResetFilters = () => {
    setFilters(EMPTY_FILTERS);
    setCurrentPage(1);
  };
  const handlePageChange = (p) => {
    setCurrentPage(p);
  };
  // Tri serveur : 1er clic → desc, 2e clic même colonne → asc (toggle). Reset page.
  const handleSort = (key) => {
    setFilters((prev) => {
      const active = prev.sort_by === key;
      const order = active && prev.sort_order === 'desc' ? 'asc' : 'desc';
      return { ...prev, sort_by: key, sort_order: order };
    });
    setCurrentPage(1);
  };

  const handleDelete = async (token, e) => {
    e.stopPropagation();
    const name = token.name || token.symbol || 'ce token';
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer ${name} ?\n\nCette action est irréversible.`)) return;
    setDeletingTokenId(token.id);
    try {
      await deleteToken(token.id);
      refetchTokens();
    } catch (err) {
      alert(`Erreur lors de la suppression: ${err.response?.data?.detail || err.message}`);
    } finally {
      setDeletingTokenId(null);
    }
  };

  const t = clock.toLocaleTimeString('fr-FR', { hour12: false, timeZone: 'Europe/Paris' });

  const startIndex = totalTokens === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1;
  const endIndex = Math.min((currentPage - 1) * itemsPerPage + tokens.length, totalTokens);

  const pageNumbers = [...Array(Math.min(totalPages, 5))].map((_, idx) => {
    if (totalPages <= 5) return idx + 1;
    if (currentPage <= 3) return idx + 1;
    if (currentPage >= totalPages - 2) return totalPages - 4 + idx;
    return currentPage - 2 + idx;
  });

  return (
    <div className="ck">
      <style>{CSS}</style>

      <NavRail />

      <div className="main">
        <div className="top">
          <div className="top-title"><span className="ic">◎</span> Tokens détectés</div>
          <div className="top-spacer" />
          <div className="clock"><b>{t}</b><br />Europe/Paris</div>
        </div>

        <div className="page">
          <div className="bar-row">
            <span className="h1">
              Flux de scan
              {tokensFetching && <span className="scan-live"><span className="d" /> sync…</span>}
            </span>
            <button className="refresh" onClick={() => refetchTokens()} disabled={tokensFetching}>
              ↻ Rafraîchir
            </button>
          </div>

          {/* Stats SENSOR réelles (getStats — agrégats sur les features denses) */}
          <div className="rules">
            <div className="rule"><div className="k">Total tokens</div><div className="v">{(stats.total_tokens ?? totalTokens).toLocaleString('fr-FR')}</div></div>
            <div className="rule"><div className="k">Bonding moyen</div><div className="v cyan">{stats.avg_bonding_pct != null ? `${stats.avg_bonding_pct}%` : '—'}</div></div>
            <div className="rule"><div className="k">Liq moyenne</div><div className="v">{stats.avg_liq_sol != null ? `${stats.avg_liq_sol} SOL` : '—'}</div></div>
            <div className="rule"><div className="k">Wallets moyen</div><div className="v">{stats.avg_wallets != null ? stats.avg_wallets : '—'}</div></div>
            <div className="rule"><div className="k">Bonding ≥ 50%</div><div className="v green">{(stats.near_migration_count ?? 0).toLocaleString('fr-FR')}</div></div>
            <div className="rule"><div className="k">Rug élevé (≥20)</div><div className="v red">{(stats.high_rug_count ?? 0).toLocaleString('fr-FR')}</div></div>
          </div>

          {/* Filtres réels complets */}
          <div className="filters-wrap">
            <Filters
              filters={filters}
              onFilterChange={handleFilterChange}
              onReset={handleResetFilters}
              chains={chains || []}
              sources={sources || []}
            />
          </div>

          {/* Filtres outcomes (peak/migré/rug/timing) — token_outcomes ⋈ bc_pda */}
          <OutcomesBar filters={filters} onChange={handleFilterChange} />

          {/* Tableau flux de scan */}
          <div className="tbl-wrap">
            <div className="tbl-hd">
              <span className="ti">Flux de scan · tokens détectés</span>
              <span className="ct">
                {totalTokens === 0 ? '—' : `${startIndex}–${endIndex} / ${totalTokens.toLocaleString('fr-FR')}`}
                {' · '}
                <span style={{ color: 'var(--green)' }}>auto {counts.ACHAT_AUTO || 0}</span> ·
                <span style={{ color: 'var(--cyan)' }}> manuel {counts.ACHAT_MANUEL || 0}</span> ·
                <span style={{ color: 'var(--red)' }}> rejet {counts.REJET || 0}</span>
              </span>
            </div>

            {tokensLoading ? (
              <div className="state"><div className="spinner" /><p>Chargement des tokens…</p></div>
            ) : tokensError ? (
              <div className="state">
                <div className="ic">⚠️</div>
                <h3>Impossible de charger les tokens</h3>
                <p>{String(tokensErrorObj?.message || 'Erreur de chargement')}</p>
                <button className="refresh" style={{ marginTop: 10 }} onClick={() => refetchTokens()}>Réessayer</button>
              </div>
            ) : tokens.length === 0 ? (
              <div className="state">
                <div className="ic">🔍</div>
                <h3>Aucun token trouvé</h3>
                <p>Essayez de modifier les filtres</p>
              </div>
            ) : (
              <div style={{ maxHeight: '78vh', overflow: 'auto' }}>
                <table>
                  <thead>
                    <tr>
                      {SORT_COLS.map((c) => {
                        const sorted = filters.sort_by === c.key;
                        return (
                          <th
                            key={c.key}
                            className={`sortable ${sorted ? 'sorted' : ''} ${c.align === 'left' ? '' : ''}`}
                            onClick={() => handleSort(c.key)}
                            title="Trier (clic pour inverser)"
                          >
                            {c.label}
                            <span className="arr">{sorted ? (filters.sort_order === 'desc' ? '▼' : '▲') : '↕'}</span>
                          </th>
                        );
                      })}
                      <th>Créateur</th>
                      <th className="act">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tokens.map((x) => {
                      const mint = x.address ? `${x.address.slice(0, 4)}…${x.address.slice(-4)}` : '—';
                      const bond = x.bonding_curve_progress;
                      const bondPct = bond != null ? bond * 100 : null;
                      return (
                        <tr key={x.id} onClick={() => setSelectedTokenId(x.id)}>
                          <td>
                            <div className="tk">
                              <b>{x.name || 'Sans nom'}</b>
                              <span className="sym">{x.symbol || 'N/A'} · {(x.chain || '').toUpperCase()}</span>
                              <span className="mint">{mint}<CopyBtn text={x.address} /></span>
                            </div>
                          </td>
                          <td>
                            {x.pnl_pct != null ? (
                              <div className="tk">
                                <b style={{ color: x.pnl_pct > 0 ? 'var(--green)' : x.pnl_pct < 0 ? 'var(--red)' : 'var(--text)' }}>
                                  {x.pnl_pct > 0 ? '+' : ''}{x.pnl_pct.toFixed(1)}%
                                </b>
                                <span className="sym">{x.n_trades != null ? `${x.n_trades} trade${x.n_trades > 1 ? 's' : ''}` : ''}</span>
                              </div>
                            ) : <span className="muted">—</span>}
                          </td>
                          <td>
                            {bondPct == null ? <span className="muted">—</span> : (
                              <div className="curve">
                                <span>{bondPct.toFixed(1)}%</span>
                                <div className="curve-bar">
                                  <div className="curve-fill" style={{ width: `${Math.max(0, Math.min(100, bondPct))}%` }} />
                                </div>
                              </div>
                            )}
                          </td>
                          <td>{fmtSol(x.real_liq_sol)}</td>
                          <td>{x.unique_wallets != null ? fmtNum(x.unique_wallets) : '—'}</td>
                          <td style={{ color: rugColor(x.rug_score), fontWeight: 600 }}>
                            {x.rug_score != null ? x.rug_score.toFixed(1) : '—'}
                          </td>
                          <td className="muted">{x.buy_speed != null ? x.buy_speed.toFixed(1) : '—'}</td>
                          <td className="muted">{fmtPriceSol(x.price_sol)}</td>
                          <td className="muted">{fmtAge(ageMinFromCreated(x.created_at))}</td>
                          <td>
                            {x.oc_peak != null ? (
                              <span style={{ color: x.oc_peak >= 0 ? 'var(--green)' : 'var(--red)', fontWeight: 600 }}>
                                {x.oc_peak >= 0 ? '+' : ''}{x.oc_peak.toFixed(1)}%
                              </span>
                            ) : <span className="muted">—</span>}
                            {x.oc_migrated && <span style={{ marginLeft: 5, fontSize: 9, color: 'var(--cyan)', fontWeight: 700 }}>migré</span>}
                            {x.oc_rugged && <span style={{ marginLeft: 5, fontSize: 9, color: 'var(--red)', fontWeight: 700 }}>rug</span>}
                          </td>
                          <td onClick={(e) => e.stopPropagation()}>
                            {x.oc_creator ? (
                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                                <span className="mint" title={x.oc_creator} style={{ cursor: 'pointer' }} onClick={() => navigator.clipboard?.writeText(x.oc_creator)}>
                                  {`${x.oc_creator.slice(0, 4)}…${x.oc_creator.slice(-4)}`}
                                </span>
                                <CopyBtn text={x.oc_creator} />
                                <a href={`https://gmgn.ai/sol/address/${x.oc_creator}`} target="_blank" rel="noopener noreferrer" style={{ fontSize: 10, color: 'var(--amber)', textDecoration: 'none', fontWeight: 700 }}>GM</a>
                              </span>
                            ) : <span className="muted">—</span>}
                          </td>
                          <td>
                            <div className="acts">
                              <a
                                className="act-btn"
                                href={`https://dexscreener.com/${x.chain}/${x.ui_mint || x.address}`}
                                target="_blank" rel="noopener noreferrer"
                                title="DexScreener"
                                onClick={(e) => e.stopPropagation()}
                              >🔗</a>
                              <button
                                className="act-btn del"
                                onClick={(e) => handleDelete(x, e)}
                                disabled={deletingTokenId === x.id}
                                title="Supprimer ce token"
                              >{deletingTokenId === x.id ? '⏳' : '🗑'}</button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {totalPages > 1 && (
              <div className="pager">
                <button className="pg-btn" onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1}>← Préc.</button>
                {pageNumbers.map((n) => (
                  <button key={n} className={`pg-num ${currentPage === n ? 'active' : ''}`} onClick={() => handlePageChange(n)}>{n}</button>
                ))}
                <span className="pg-info">Page {currentPage} / {totalPages}</span>
                <button className="pg-btn" onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages}>Suiv. →</button>
              </div>
            )}
          </div>
        </div>
      </div>

      {selectedTokenId && (
        <TokenModal
          tokenId={selectedTokenId}
          onClose={() => setSelectedTokenId(null)}
        />
      )}
    </div>
  );
}

export default TokensPage;
